/**
 * Local → strategic sync (M4 spec §10.4, plan I4): every `syncS` seconds the local agents' hp, cargo and step
 * progress go to the core in one `syncLocal` batch, and a `deaths`-stage reader turns the sim's `kill` events of
 * tracked real ships into `localDestroyed(agentId, killer)`: `'player'` when the player fired the killing shot, the
 * attacker's faction when it is traffic, otherwise null. The destroyed agent leaves the store at once. The reader also
 * keeps who opened each exchange of fire with the player (`charter/crimeDetector.ts` `EngagementLog`) and adds
 * `selfDefence: true` to a player kill of a ship that fired first (M7 S1, D-339: no Holdfast cost for that guardian).
 *
 * The batch is built at message rate (every `syncS`); the kill reader allocates nothing unless a tracked ship died.
 */
import { createEngagementLog, firedFirst, forgetEngagement, noteEngagement, type EngagementLog } from '../charter/crimeDetector'
import { EV, eventSlot, firstReadable } from '../combat/events'
import type { Sim, SimStageFn } from '../sim'
import type { FactionId, Killer, LocalSync, StrategicCommand } from '../strategic/types'
import { Agent } from './components'
import { removeAgent, type ContactStore } from './contacts'
import type { TierState } from './realShips'

export interface SyncState {
  /** Seconds since the last batch. */
  elapsed: number
}

export function createSyncState(): SyncState {
  return { elapsed: 0 }
}

/** Builds the batch for every agent in the store (message rate). */
export function buildSyncBatch(s: ContactStore): LocalSync[] {
  const batch: LocalSync[] = []
  for (let i = 0; i < s.count; i++) batch.push({ id: s.ids[i], hp: s.hp[i], cargoQty: s.cargoQty[i], progress: s.progress[i] })
  return batch
}

/**
 * Advances the sync timer by `dt` seconds; every `syncS` sends one batch (skipped when the store is empty).
 * Returns true when a batch went out.
 */
export function stepSync(st: SyncState, s: ContactStore, dt: number, syncS: number, send: (batch: readonly LocalSync[]) => void): boolean {
  st.elapsed += dt
  if (st.elapsed < syncS) return false
  st.elapsed = 0
  if (s.count === 0) return false
  send(buildSyncBatch(s))
  return true
}

/** The slot of real ship `eid`, or −1 when the store does not track it. */
function slotOf(s: ContactStore, eid: number): number {
  if (eid < 0) return -1
  const slot = Agent.index[eid]
  return slot >= 0 && slot < s.count && s.eid[slot] === eid ? slot : -1
}

/** Who killed `victimSlot`'s ship: the player, a traffic ship's faction, or null. */
export function killerOf(sim: Sim, s: ContactStore, attacker: number): Killer {
  if (attacker >= 0 && attacker === sim.playerEid) return 'player'
  const slot = slotOf(s, attacker)
  return slot >= 0 ? s.faction[slot] : null
}

/** M7 S37: told of every tracked real ship the player killed (the story link's `shipKilled`); null = nobody. */
export type PlayerKillListener = (agentId: string, faction: FactionId) => void

let playerKill: PlayerKillListener | null = null

/** Binds (or with null, unbinds) the player-kill listener (the story link). */
export function setPlayerKillListener(fn: PlayerKillListener | null): void {
  playerKill = fn
}

/**
 * The `deaths`-stage reader (register with `registerSimStage(sim, 'deaths', ...)`): reports each tracked real
 * ship's `kill` and removes its agent. Allocation-free unless a tracked ship died.
 */
export function createKillReader(
  s: ContactStore,
  tier: TierState,
  send: (cmd: StrategicCommand) => void,
  engagements: EngagementLog = createEngagementLog(),
): SimStageFn & { cursor: number } {
  const reader = (sim: Sim): void => {
    const ring = sim.events
    const now = sim.time
    for (let i = firstReadable(ring, reader.cursor); i < ring.head; i++) {
      const e = eventSlot(i)
      const type = ring.type[e]
      noteEngagement(engagements, type, ring.eid[e], ring.other[e], sim.playerEid, now)
      if (type !== EV.kill) continue
      const victim = ring.eid[e]
      const selfDefence = firedFirst(engagements, victim, now)
      forgetEngagement(engagements, victim)
      const slot = slotOf(s, victim)
      if (slot < 0) continue
      const killer = killerOf(sim, s, ring.other[e])
      if (killer === 'player' && selfDefence) send({ type: 'localDestroyed', agentId: s.ids[slot], killer, selfDefence: true })
      else send({ type: 'localDestroyed', agentId: s.ids[slot], killer })
      if (killer === 'player') playerKill?.(s.ids[slot], s.faction[slot])
      s.eid[slot] = -1
      tier.real--
      removeAgent(s, slot)
    }
    reader.cursor = ring.head
  }
  reader.cursor = 0
  return reader
}
